import type { UseStepperReturn } from "@vueuse/core"
import type { ExternalNames, ImportData, ItemInfo, NewNames, Step, UploadFile } from "@/types"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { useFileDialog, useStepper } from "@vueuse/core"
import { defineStore } from "pinia"
import { markRaw } from "vue"
import Locate from "@/components/ListImport/Locate.vue"
import Preview from "@/components/ListImport/Preview.vue"
import { Decorator, ItemMatchStatus } from "@/types"
import { objectToCsv, removeDatasetPrefix, stringArrayToLower, stringKeyedMapToLower } from "@/utils"
import { EXTERNAL_NAME_SUFFIX } from "@/utils/alphaModel"
import { autoLocateColumnHeaders } from "@/utils/fileImport/autolocate"
import { readCSV, readXlsxFile } from "@/utils/fileImport/upload"

type LocateFileTarget = Map<string, number | undefined>
type ItemMatchInfo = Map<string, ItemInfo[]>

interface State {
  loading: boolean
  model?: ModelFacade
  dialog: boolean
  file?: UploadFile
  target?: string
  importTargets: string[]
  locateInfo: LocateFileTarget
  itemMatchInfo: ItemMatchInfo
}

export enum PhaseSteps {
  UPLOAD = "Target and upload",
  SELECT = "Select",
  PREVIEW = "Preview",
}

const listImportDialog = useFileDialog({ accept: ".tsv, .csv, .txt, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })

export const useListImportStore = defineStore("listImport", {
  state: (): State => ({
    loading: false,
    model: undefined,
    dialog: false,
    file: undefined,
    target: undefined,
    importTargets: [],
    locateInfo: new Map(),
    itemMatchInfo: new Map(),
  }),
  actions: {
    initialise(model: ModelFacade) {
      this.model = model

      listImportDialog.onChange(async (files) => {
        if (files && files.length > 0) {
          this.loading = true
          this.dialog = true
          try {
            await this.loadFiles(files)
            await this.autoLocate()
          } catch (error) {
            console.error(error)
          }

          this.loading = false
        }
      })
    },

    reset() {
      // Temporary store some states.
      const persistKeys: Array<keyof State> = [
        "model",
      ]

      const persistedValues = Object.fromEntries(persistKeys.map((key) => [key, this[key]]))

      // Reset the store to the default state.
      this.$reset()

      // Restore persisted values.
      this.$patch(persistedValues)

      listImportDialog.reset()

      // Reset the stepper to go back to the first step.
      this.stepper.goTo(this.steps[0])
    },
    closeDialog() {
      this.dialog = false
      // Reset import when dialog closes.
      this.reset()
    },

    async loadFiles(files: FileList) {
      const firstFile = files.item(0)

      if (firstFile === null) {
        throw new Error("No file uploaded")
      }

      let file: UploadFile

      if (
        firstFile.type === "text/csv" ||
        firstFile.type === "text/tab-separated-values" ||
        // Firefox uses the registered application to determine the MIME type.
        firstFile.name.endsWith(".csv") ||
        firstFile.name.endsWith(".tsv")
      ) {
        file = await readCSV(firstFile)
      } else {
        file = await readXlsxFile(firstFile)
      }

      this.addFile(file)
    },

    addFile(file: UploadFile) {
      this.file = file
    },

    // Steps
    goToNext() {
      this.stepper.goToNext()
    },

    goToStep(phaseStep: PhaseSteps) {
      const foundStep = this.stepper.steps.value.find((step) => step.title === phaseStep)
      if (!foundStep) {
        console.warn("Step not found", phaseStep)
        return
      }

      this.stepper.goTo(foundStep)
    },

    async getImportTargets() {
      const importTargets = await this.model!.getImportTargets()
      this.importTargets = importTargets.categories

      if (this.target === undefined) {
        this.setTarget("Selling item") // TODO: Remove hardcoding - just for testing
      }
    },
    async getTargetAttributes(target: string) {
      if (target === undefined) {
        throw new Error("No target set")
      }

      const targetAttributes = await this.modelApi.defaultAttributes(target)

      // Set up map for each target attribute
      for (let i = 0; i < targetAttributes.length; i++) {
        const attribute = targetAttributes[i]

        this.locateInfo.set(removeDatasetPrefix(attribute.name), undefined)
      }
    },
    async setTarget(target: string) {
      this.target = target

      // Reset map when target changes
      this.locateInfo = new Map<string, number>()

      this.locateInfo.set(this.target, undefined)

      if (this.importingExternalNames) {
        this.locateInfo.set(this.target.replace(EXTERNAL_NAME_SUFFIX, ""), undefined)
      } else {
        await this.getTargetAttributes(this.target)
        await this.autoLocate()
      }
    },
    isColumnEmpty(columnIndex: number): boolean {
      const nonBlankValues = this.getValuesInColumn(columnIndex).filter(
        (item) => item.trim().length !== 0
      )

      return nonBlankValues.length === 0
    },
    getValuesInColumn(columnIndex: number): string[] {
      if (!this.file?.scraped) {
        return []
      }
      return this.file.scraped.rows.map((row) => row[columnIndex])
    },
    async autoLocate() {
      if (!this.file) {
        // Return early if no file is uploaded yet, don't throw error
        // watch should handle this
        return
      }
      if (!this.target) {
        throw new Error("No target selected to auto locate")
      }

      autoLocateColumnHeaders(this.modelApi, this.file, [this.target], [...this.locateInfo.keys()])
        .forEach((columnIndex, targetName) => {
          // Locate attributes
          if (this.locateInfo.has(targetName)) {
            this.locateInfo.set(targetName, columnIndex)
          }
        })

      await this.populateMatchDataFromLocate()
    },
    fileSelect() {
      listImportDialog.open()
    },
    /**
     * Populate names
     */
    async populateMatchDataFromLocate() {
      if (!this.file?.scraped) {
        console.warn("file missing, no matches possible")
        return
      }
      if (this.locateInfo.size === 0) {
        console.warn("locate mappings missing, no matches possible")
        return
      }
      const data = this.file?.scraped.rows
      const categories = Array.from(this.locateInfo.keys())

      // Clear old match data when changing Locate
      // This is likely not performant if the locate targets haven't changed
      // As it relocates everything, rather than just the category that has changed.
      this.itemMatchInfo.clear()

      // Assume from sample files in https://miro.com/app/board/uXjVMcmytoo=/?moveToWidget=3458764549466035793&cot=14
      // that there will be the same number of rows for all columns
      for (const categoryName of categories) {
        const colIndex = this.locateInfo.get(categoryName)!
        const uniqueItems = this.extractUniqueItems(colIndex, data)

        const externalNames: ItemInfo[] = uniqueItems.map((name) => {
          return {
            externalName: name,
            status: ItemMatchStatus.CREATE,
          }
        })

        this.itemMatchInfo.set(categoryName, externalNames)

        // Special casing for import external names
        if (!this.importingExternalNames) {
          await this.populateMatchStatus(categoryName)
        }
      }
    },

    setMatchStatus(
      categoryName: string,
      rowIndex: number,
      itemName: string,
      status: ItemMatchStatus
    ) {
      const categoryItems = this.itemMatchInfo.get(categoryName)

      if (categoryItems === undefined) {
        return
      }

      categoryItems[rowIndex].internalName = itemName
      categoryItems[rowIndex].status = status
      this.itemMatchInfo.set(categoryName, categoryItems)
    },
    extractUniqueItems(colIndex: number, rows: string[][]) {
      if (colIndex === undefined) {
        return []
      }
      const uniqueItems: Set<string> = new Set()

      rows.forEach((row) => uniqueItems.add(row[colIndex].trim()))

      uniqueItems.delete("") // remove any blank

      return Array.from(uniqueItems)
    },
    async populateMatchStatus(categoryName: string) {
      const [itemNames, externalNames, ignoredNames]: [
        itemNames: string[],
        externalNames: Map<string, string>,
        ignoredNames: string[],
      ] = await Promise.all([
        this.modelApi.itemNames(categoryName),
        this.modelApi.externalNameMap(categoryName),
        this.modelApi.ignoredNames(categoryName),
      ])

      const itemMatchInfoForCategory = this.setItemMatchStatus(
        this.itemMatchInfo.get(categoryName) ?? [],
        itemNames,
        externalNames,
        ignoredNames
      )

      this.itemMatchInfo.set(categoryName, itemMatchInfoForCategory)
    },

    setItemMatchStatus(
      itemMatchInfo: ItemInfo[],
      itemNames: string[],
      externalNames: Map<string, string>,
      ignoredNames: string[]
    ) {
      // TODO(Matt): This only works with specific locales which don't require case folding / normalisation.
      // investigate localeCompare https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
      const lowerItemNames = stringArrayToLower(itemNames)
      const lowerExternalNames = stringKeyedMapToLower(externalNames)
      const lowerIgnoredNames = stringArrayToLower(ignoredNames)

      return itemMatchInfo.map((itemInfo) => {
        const externalName = itemInfo.externalName.toLowerCase()

        if (lowerExternalNames?.has(externalName)) {
          // Exact match between name in the file and external name. User has
          // matched Ecuador Bananas to Bananas in a previous import.
          // Status is: Existing Match

          itemInfo.internalName = lowerExternalNames.get(externalName) as
          | string |
          undefined
          itemInfo.status = ItemMatchStatus.EXISTING_MATCH
        } else if (lowerIgnoredNames.includes(externalName)) {
          // Set status of previously ignored items
          itemInfo.status = ItemMatchStatus.ALREADY_IGNORED
        } else if (lowerItemNames.includes(externalName)) {
          // Exact match between name in the file and standard name. There is no
          // matching external name. The User has not matched Banana to Banana in
          // previous import, but the name in the file is an exact match for the
          // standard name in the system. Status is: Auto-match
          itemInfo.internalName =
            itemNames[lowerItemNames?.indexOf(externalName)]
          itemInfo.status = ItemMatchStatus.AUTO_MATCH
        } else {
          itemInfo.status = ItemMatchStatus.CREATE
        }
        return itemInfo
      })
    },

    sortMatchItemsGroup(items: ItemInfo[] | undefined) {
      if (!items) {
        return []
      }

      return items.sort((a, b) => {
        if (a.externalName < b.externalName) {
          return -1
        }
        if (a.externalName > b.externalName) {
          return 1
        }
        return 0
      })
    },

    importDataIntoModel() {
      const jsonData = JSON.stringify(this.importData)
      return this.modelApi.import(jsonData)
    }

  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
    importingExternalNames(state) {
      return state.target?.endsWith(EXTERNAL_NAME_SUFFIX)
    },
    importData(state) {
      const data: Partial<ImportData> = {
        targets: state.target ? [state.target] : [],
        columnMap: Object.fromEntries(cleanLocate(state.locateInfo)),
        newNames: {
          ...newNamesFromMatchInfo(state.itemMatchInfo),
        },
        externalNames: {
          ...externalNamesFromMatchInfo(state.itemMatchInfo),
        },
        data: state.file?.scraped ? objectToCsv(state.file?.scraped) : "",
      }

      return data
    },
    allowImport(state) {
      if (state.target === undefined || !(state.target in (this.importData.columnMap ?? {}))) {
        return false
      }

      return true
    },
    steps(): Step<PhaseSteps>[] {
      return [
        {
          title: PhaseSteps.SELECT,
          component: markRaw(Locate),
          showNext: () => true,
          decorator: () => Decorator.NOT_APPLICABLE,
        },
        {
          title: PhaseSteps.PREVIEW,
          component: markRaw(Preview),
          showNext: () => true,
          decorator: () => Decorator.NOT_APPLICABLE,
        },
      ]
    },
    stepper(): UseStepperReturn<any, Step<PhaseSteps>[], Step<PhaseSteps>> {
      return useStepper<any>(this.steps)
    },
  }
})

function newNamesFromMatchInfo(itemMatchInfo: ItemMatchInfo): NewNames {
  const newNames: NewNames = {}

  for (const [key, value] of itemMatchInfo) {
    // Find all the items to match
    const items = value.filter(
      (item: ItemInfo) => item.status === ItemMatchStatus.CREATE
    )

    if (items.length > 0) {
      // Format items to and object for the engine
      // { DATASET_NAME: [ names ]}
      newNames[key] = items.map((item) => item.externalName)
    }
  }

  return newNames
}

function externalNamesFromMatchInfo(
  itemMatchInfo: ItemMatchInfo
): ExternalNames {
  const externalNames: ExternalNames = {}

  for (const [key, value] of itemMatchInfo) {
    // Find all the items to match
    const items = value.filter((item: ItemInfo) =>
      [
        ItemMatchStatus.AUTO_MATCH,
        ItemMatchStatus.MANUAL_MATCH,
        ItemMatchStatus.CREATE,
        ItemMatchStatus.EDIT_MATCH,
      ].includes(item.status)
    )

    if (items.length > 0) {
      // Format items to and object for the engine
      // { DATASET_NAME: { EXTERNAL_NAME : INTERNAL_NAME } }
      externalNames[key] = items.reduce(
        (result: Record<string, string>, item: ItemInfo) => {
          result[item.externalName] = item.internalName as string
          return result
        },
        {}
      )
    }
  }

  return externalNames
}

function cleanLocate(locateInfo: LocateFileTarget): Map<string, number> {
  const cleanedArray = [...locateInfo].filter(([_key, value]) => value !== undefined)

  const cleanedMap = new Map(cleanedArray)
  return cleanedMap as Map<string, number>
}
